// Begehungs-Modul — strukturierte Betriebsbegehung (§77 ASchG):
// Mängel einzeln erfassen (Priorität, Maßnahme, Frist, Foto) → Protokoll-PDF
// mit Fotos + Fristen-Nachverfolgung.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useMandantStore } from '../../store/mandantStore';
import { useHandoffStore } from '../../store/handoffStore';
import { prepareAnhang } from '../../services/captureService';
import ErinnerungButton from '../../components/ErinnerungButton';
import {
  loadBegehungen, saveBegehung, neueBegehung, neuerMangel,
  offeneMaengel, toggleMangelErledigt, PRIORITAET_META,
  type Begehung, type Mangel, type OffenerMangel,
} from '../../services/begehungService';
import { SpeechTextarea } from '../../components/form/SpeechToTextButton';

function fmt(iso: string): string {
  try { return new Date(iso + (iso.length === 10 ? 'T00:00:00' : '')).toLocaleDateString('de-AT'); }
  catch { return iso; }
}

// ── Mangel-Editor (eine Karte) ─────────────────────────────────────────────

function MangelKarte({ mangel, index, onChange, onDelete }: {
  mangel: Mangel;
  index: number;
  onChange: (m: Mangel) => void;
  onDelete: () => void;
}): React.JSX.Element {
  const fileRef = useRef<HTMLInputElement>(null);
  const [fotoBusy, setFotoBusy] = useState(false);

  const setFoto = async (file: File | undefined): Promise<void> => {
    if (file === undefined) return;
    setFotoBusy(true);
    try {
      const a = await prepareAnhang(file);
      onChange({ ...mangel, foto_b64: a.b64, foto_mime: a.mime });
    } catch { /* ignore */ }
    setFotoBusy(false);
  };

  return (
    <div className="card space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold text-gray-400">Mangel {index + 1}</span>
        <button onClick={onDelete} className="text-xs text-red-500 hover:text-red-700">Entfernen</button>
      </div>
      <div className="space-y-1">
        <label className="label-text">Beschreibung *</label>
        <SpeechTextarea value={mangel.beschreibung} rows={2}
          onChange={v => { onChange({ ...mangel, beschreibung: v }); }}
          placeholder="Was wurde festgestellt und wo?" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="label-text">Priorität</label>
          <select value={mangel.prioritaet}
            onChange={e => { onChange({ ...mangel, prioritaet: e.target.value as Mangel['prioritaet'] }); }}
            className="input-field text-sm">
            <option value="HOCH">Hoch</option>
            <option value="MITTEL">Mittel</option>
            <option value="NIEDRIG">Niedrig</option>
          </select>
        </div>
        <div className="space-y-1">
          <label className="label-text">Frist</label>
          <input type="date" value={mangel.frist ?? ''}
            onChange={e => { onChange({ ...mangel, frist: e.target.value }); }}
            className="input-field text-sm" />
        </div>
      </div>
      <div className="space-y-1">
        <label className="label-text">Maßnahme</label>
        <SpeechTextarea value={mangel.massnahme} rows={2}
          onChange={v => { onChange({ ...mangel, massnahme: v }); }}
          placeholder="Empfohlene Maßnahme zur Behebung" />
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden"
          onChange={e => { void setFoto(e.target.files?.[0]); }} />
        <button onClick={() => { fileRef.current?.click(); }} disabled={fotoBusy}
          className="btn-secondary text-sm disabled:opacity-50">
          {fotoBusy ? '📷 …' : mangel.foto_b64 !== undefined ? '📷 Foto ersetzen' : '📷 Foto'}
        </button>
        {mangel.foto_b64 !== undefined && (
          <>
            <img src={`data:${mangel.foto_mime};base64,${mangel.foto_b64}`} alt="Mangel"
              className="h-14 w-14 object-cover rounded-lg border border-gray-200 dark:border-gray-700" />
            <button onClick={() => {
              const { foto_b64: _b, foto_mime: _m, ...rest } = mangel;
              onChange(rest);
            }}
              className="text-xs text-gray-400 hover:text-red-500">Foto entfernen</button>
          </>
        )}
        <label className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300 ml-auto cursor-pointer">
          <input type="checkbox" checked={mangel.erledigt}
            onChange={e => { onChange({ ...mangel, erledigt: e.target.checked }); }}
            className="rounded border-gray-300 text-green-600" />
          erledigt
        </label>
      </div>
    </div>
  );
}

// ── Begehungs-Editor ───────────────────────────────────────────────────────

function BegehungEditor({ initial, mandantName, onSaved, onCancel, onPrint }: {
  initial: Begehung;
  mandantName: string;
  onSaved: () => void;
  onCancel: () => void;
  onPrint: (b: Begehung) => void;
}): React.JSX.Element {
  const [b, setB] = useState<Begehung>(initial);

  const setMangel = (id: string, m: Mangel): void => {
    setB(prev => ({ ...prev, maengel: prev.maengel.map(x => x.id === id ? m : x) }));
  };
  const addMangel = (): void => { setB(prev => ({ ...prev, maengel: [...prev.maengel, neuerMangel()] })); };
  const delMangel = (id: string): void => { setB(prev => ({ ...prev, maengel: prev.maengel.filter(x => x.id !== id) })); };

  const speichern = (): void => { void saveBegehung(b).then(onSaved); };

  return (
    <div className="p-6 space-y-5 max-w-3xl mx-auto">
      <div className="flex items-center gap-3">
        <button onClick={onCancel} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400">←</button>
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Begehung</h1>
          <p className="text-xs text-gray-400">{mandantName}</p>
        </div>
      </div>

      <div className="card space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="label-text">Begehungsdatum *</label>
            <input type="date" value={b.datum} onChange={e => { setB({ ...b, datum: e.target.value }); }}
              className="input-field" />
          </div>
          <div className="space-y-1">
            <label className="label-text">Begehter Bereich</label>
            <input value={b.bereich} onChange={e => { setB({ ...b, bereich: e.target.value }); }}
              placeholder="z.B. Produktion Halle 2" className="input-field" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="label-text">Beginn (Uhrzeit)</label>
            <input type="time" value={b.uhrzeit ?? ''} onChange={e => { setB({ ...b, uhrzeit: e.target.value }); }}
              className="input-field" />
          </div>
          <div className="space-y-1">
            <label className="label-text">Ende (Uhrzeit)</label>
            <input type="time" value={b.uhrzeit_ende ?? ''} onChange={e => { setB({ ...b, uhrzeit_ende: e.target.value }); }}
              className="input-field" />
          </div>
        </div>
        <div className="space-y-1">
          <label className="label-text">Ort / Standort</label>
          <input value={b.ort ?? ''} onChange={e => { setB({ ...b, ort: e.target.value }); }}
            placeholder="z.B. Firmenadresse / Betriebsstätte" className="input-field" />
        </div>
        <div className="space-y-1">
          <label className="label-text">Begleitung vor Ort</label>
          <input value={b.begleiter} onChange={e => { setB({ ...b, begleiter: e.target.value }); }}
            placeholder="Sicherheitsfachkraft, Betriebsrat…" className="input-field" />
        </div>
        <div className="space-y-1">
          <label className="label-text">Allgemeine / positive Feststellungen</label>
          <SpeechTextarea value={b.allgemein} rows={2}
            onChange={v => { setB({ ...b, allgemein: v }); }} />
        </div>
      </div>

      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-gray-900 dark:text-gray-100">
          Mängel <span className="text-sm text-gray-400">({b.maengel.length})</span>
        </h2>
        <button onClick={addMangel} className="btn-secondary text-sm">+ Mangel</button>
      </div>

      {b.maengel.length === 0 ? (
        <div className="card text-center py-8 text-gray-400 text-sm">
          Noch keine Mängel — „+ Mangel" oder direkt mit Foto erfassen.
        </div>
      ) : (
        <div className="space-y-3">
          {b.maengel.map((m, i) => (
            <MangelKarte key={m.id} mangel={m} index={i}
              onChange={mm => { setMangel(m.id, mm); }}
              onDelete={() => { delMangel(m.id); }} />
          ))}
        </div>
      )}

      <div className="flex gap-2 flex-wrap sticky bottom-0 bg-gray-50/90 dark:bg-gray-950/90 py-3 -mx-6 px-6 backdrop-blur">
        <button onClick={speichern} className="btn-primary">✓ Speichern</button>
        <button onClick={() => { void saveBegehung(b).then(() => { onPrint(b); }); }} className="btn-secondary">
          🖨 Protokoll drucken
        </button>
        <button onClick={onCancel} className="btn-secondary ml-auto">Abbrechen</button>
      </div>
    </div>
  );
}

// ── Protokoll (druckbar) ───────────────────────────────────────────────────

function Protokoll({ b, mandantName, onBack }: {
  b: Begehung; mandantName: string; onBack: () => void;
}): React.JSX.Element {
  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div data-no-print className="flex gap-2 mb-5">
        <button onClick={onBack} className="btn-secondary text-sm">← Zurück</button>
        <button onClick={() => { window.print(); }} className="btn-primary text-sm">🖨 Drucken / PDF</button>
      </div>

      <div className="bg-white text-black p-2 space-y-4 print:p-0">
        <div className="border-b-2 border-gray-800 pb-2">
          <h1 className="text-xl font-bold">Begehungsprotokoll</h1>
          <p className="text-sm">gem. §77 ASchG · {mandantName}</p>
        </div>
        <table className="text-sm">
          <tbody>
            <tr><td className="pr-4 font-medium align-top">Datum:</td><td>{fmt(b.datum)}</td></tr>
            <tr><td className="pr-4 font-medium align-top">Uhrzeit:</td><td>{b.uhrzeit ? `${b.uhrzeit}${b.uhrzeit_ende ? `–${b.uhrzeit_ende}` : ''} Uhr` : '—'}</td></tr>
            <tr><td className="pr-4 font-medium align-top">Ort:</td><td>{b.ort || '—'}</td></tr>
            <tr><td className="pr-4 font-medium align-top">Bereich:</td><td>{b.bereich || '—'}</td></tr>
            <tr><td className="pr-4 font-medium align-top">Begleitung:</td><td>{b.begleiter || '—'}</td></tr>
          </tbody>
        </table>
        {b.allgemein !== '' && (
          <div>
            <h2 className="font-semibold text-sm border-b border-gray-300">Allgemeine Feststellungen</h2>
            <p className="text-sm whitespace-pre-wrap mt-1">{b.allgemein}</p>
          </div>
        )}
        <div>
          <h2 className="font-semibold text-sm border-b border-gray-300 mb-2">
            Festgestellte Mängel ({b.maengel.length})
          </h2>
          {b.maengel.length === 0 ? (
            <p className="text-sm italic">Keine Mängel festgestellt.</p>
          ) : (
            <div className="space-y-3">
              {b.maengel.map((m, i) => (
                <div key={m.id} className="border border-gray-300 rounded p-2 break-inside-avoid">
                  <div className="flex items-baseline gap-2">
                    <span className="font-bold text-sm">{i + 1}.</span>
                    <span className="text-sm font-medium flex-1">{m.beschreibung || '—'}</span>
                    <span className="text-xs font-bold uppercase">
                      {PRIORITAET_META[m.prioritaet].label}
                      {m.erledigt ? ' · erledigt' : ''}
                    </span>
                  </div>
                  {m.massnahme !== '' && <p className="text-sm mt-1"><span className="font-medium">Maßnahme:</span> {m.massnahme}</p>}
                  {m.frist !== undefined && m.frist !== '' && <p className="text-sm"><span className="font-medium">Frist:</span> {fmt(m.frist)}</p>}
                  {m.foto_b64 !== undefined && (
                    <img src={`data:${m.foto_mime};base64,${m.foto_b64}`} alt={`Mangel ${i + 1}`}
                      className="mt-2 max-h-56 rounded border border-gray-300" />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="pt-6 text-sm">
          <p>_______________________________</p>
          <p>Dr. med. Klaus Karrer, MSc · Arbeitsmedizin</p>
        </div>
      </div>
    </div>
  );
}

// ── Offene Mängel / Fristen ────────────────────────────────────────────────

function OffeneMaengel({ items, mandantName, onErledigt }: {
  items: OffenerMangel[];
  mandantName: (id: string) => string;
  onErledigt: (m: OffenerMangel) => void;
}): React.JSX.Element {
  if (items.length === 0) {
    return <div className="card text-center py-12 text-gray-400"><p className="text-4xl mb-2">✅</p><p className="text-sm">Keine offenen Mängel.</p></div>;
  }
  return (
    <div className="space-y-2">
      {items.map(m => (
        <div key={m.id} className={`flex items-start gap-3 px-4 py-3 rounded-lg
          ${m.ueberfaellig ? 'bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900' : 'bg-gray-50 dark:bg-gray-800'}`}>
          <input type="checkbox" checked={false} onChange={() => { onErledigt(m); }}
            className="mt-1 rounded border-gray-300 text-green-600 flex-shrink-0" title="Als erledigt markieren" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{m.beschreibung || '—'}</p>
            <p className="text-xs text-gray-400">
              {mandantName(m.mandant_id)} · {m.bereich || 'ohne Bereich'} · Begehung {fmt(m.begehung_datum)}
            </p>
          </div>
          <div className="text-right flex-shrink-0 space-y-0.5">
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PRIORITAET_META[m.prioritaet].cls}`}>
              {PRIORITAET_META[m.prioritaet].label}
            </span>
            <p className={`text-xs ${m.ueberfaellig ? 'text-red-600 font-bold' : 'text-gray-400'}`}>
              {m.frist !== undefined && m.frist !== '' ? `${m.ueberfaellig ? '⚠ ' : ''}Frist ${fmt(m.frist)}` : 'ohne Frist'}
            </p>
            <ErinnerungButton
              titel={`Frist: ${m.beschreibung || 'Mangel'}`}
              typ="FRIST"
              basisDatum={m.frist !== undefined && m.frist !== '' ? m.frist : undefined}
              mandantId={m.mandant_id}
              quelle={`begehung-mangel:${m.id}`}
              label="erinnern"
            />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Hauptkomponente ────────────────────────────────────────────────────────

type View = { mode: 'list' } | { mode: 'edit'; b: Begehung } | { mode: 'protokoll'; b: Begehung };
type Tab = 'begehungen' | 'maengel';

export function BegehungModul(): React.JSX.Element {
  const mandant = useMandantStore(s => s.getAktiverMandant());
  const mandanten = useMandantStore(s => s.mandanten);
  const consume = useHandoffStore(s => s.consume);
  const [tab, setTab]   = useState<Tab>('begehungen');
  const [view, setView] = useState<View>({ mode: 'list' });
  const [liste, setListe]   = useState<Begehung[]>([]);
  const [offen, setOffen]   = useState<OffenerMangel[]>([]);

  const mandantName = useCallback((id: string): string =>
    mandanten.find(m => m.id === id)?.name ?? id, [mandanten]);

  const reload = useCallback((): void => {
    void loadBegehungen(mandant?.id).then(setListe);
    void offeneMaengel(mandant?.id).then(setOffen);
  }, [mandant?.id]);

  useEffect(() => { reload(); }, [reload]);

  // Übergabe aus Erfassung/Akte: neue Begehung vorbefüllt öffnen
  useEffect(() => {
    const h = consume();
    if (h !== null && h.action === 'begehung' && mandant !== null && mandant !== undefined) {
      const b = neueBegehung(mandant.id);
      if (h.quelle.titel !== undefined) b.bereich = h.quelle.titel;
      if (h.quelle.text !== undefined) b.allgemein = h.quelle.text;
      setView({ mode: 'edit', b });
    }
  }, [consume, mandant]);

  if (view.mode === 'edit') {
    return (
      <BegehungEditor initial={view.b} mandantName={mandant?.name ?? '—'}
        onSaved={() => { setView({ mode: 'list' }); reload(); }}
        onCancel={() => { setView({ mode: 'list' }); reload(); }}
        onPrint={b => { setView({ mode: 'protokoll', b }); }} />
    );
  }
  if (view.mode === 'protokoll') {
    return <Protokoll b={view.b} mandantName={mandantName(view.b.mandant_id)}
      onBack={() => { setView({ mode: 'edit', b: view.b }); }} />;
  }

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-6 pt-4 flex gap-1">
        {([['begehungen', '🔍 Begehungen'], ['maengel', `🎯 Offene Mängel${offen.length > 0 ? ` (${offen.length})` : ''}`]] as const).map(([k, l]) => (
          <button key={k} onClick={() => { setTab(k); }}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors
              ${tab === k ? 'border-blue-600 text-blue-700 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-6 max-w-3xl mx-auto w-full space-y-4">
        {tab === 'begehungen' && (
          <>
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                {mandant !== null && mandant !== undefined
                  ? <>Begehungen <span className="font-medium" style={{ color: mandant.farbe }}>{mandant.name}</span></>
                  : 'Alle Firmen — bitte oben links eine Firma für neue Begehungen wählen'}
              </p>
              {mandant !== null && mandant !== undefined && (
                <button onClick={() => { setView({ mode: 'edit', b: neueBegehung(mandant.id) }); }}
                  className="btn-primary text-sm">+ Neue Begehung</button>
              )}
            </div>

            {liste.length === 0 ? (
              <div className="card text-center py-12 text-gray-400 space-y-2">
                <p className="text-4xl">🔍</p>
                <p className="font-medium text-gray-600 dark:text-gray-400">Noch keine Begehungen</p>
                <p className="text-sm">Neue Begehung starten und Mängel mit Foto + Frist erfassen.</p>
              </div>
            ) : liste.map(b => {
              const offenCount = b.maengel.filter(m => !m.erledigt).length;
              return (
                <button key={b.id} onClick={() => { setView({ mode: 'edit', b }); }}
                  className="card w-full text-left hover:shadow-md transition-all">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {b.bereich || 'Begehung'} · {fmt(b.datum)}
                      </p>
                      <p className="text-xs text-gray-400">{mandantName(b.mandant_id)}</p>
                    </div>
                    <div className="text-right text-xs flex-shrink-0">
                      <p className="text-gray-500">{b.maengel.length} Mängel</p>
                      {offenCount > 0
                        ? <p className="text-orange-600 font-medium">{offenCount} offen</p>
                        : <p className="text-green-600">alle erledigt</p>}
                    </div>
                  </div>
                </button>
              );
            })}
          </>
        )}

        {tab === 'maengel' && (
          <OffeneMaengel items={offen} mandantName={mandantName}
            onErledigt={m => { void toggleMangelErledigt(m.begehung_id, m.id).then(reload); }} />
        )}
      </div>
    </div>
  );
}
